import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, Star, Award, Crown, Medal, Shield, Target, Flame,
  Users, GraduationCap, Briefcase, Heart, CheckCircle, Clock,
  Flag, RefreshCw, Sparkles, Lock
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const ICON_MAP = {
  'door-open': Flag,
  'heart': Heart,
  'user-check': Users,
  'flame': Flame,
  'crown': Crown,
  'users': Users,
  'trophy': Trophy,
  'flag': Flag,
  'target': Target,
  'graduation-cap': GraduationCap,
  'bullseye': Target,
  'check-circle': CheckCircle,
  'star': Star,
  'clock': Clock,
  'shield-check': Shield,
  'briefcase': Briefcase,
  'building': Briefcase,
  'award': Award,
  'shield': Shield,
  'medal': Medal,
  'hand-helping': Heart,
};

export default function AchievementsPage() {
  const { axiosAuth } = useAuth();
  const [badgeData, setBadgeData] = useState(null);
  const [allBadges, setAllBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [myBadgesRes, definitionsRes] = await Promise.all([
        api.get('/badges/my-badges'),
        api.get('/badges/definitions')
      ]);
      setBadgeData(myBadgesRes.data);
      setAllBadges(definitionsRes.data);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const checkAchievements = async () => {
    setChecking(true);
    try {
      const res = await api.post('/badges/check-achievements');
      if (res.data.total_new > 0) {
        toast.success(`You earned ${res.data.total_new} new badge${res.data.total_new > 1 ? 's' : ''}!`);
        fetchData();
      } else {
        toast.info('No new badges earned yet. Keep going!');
      }
    } catch (err) {
      toast.error('Failed to check achievements');
    }
    setChecking(false);
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Award;
    return IconComponent;
  };

  const earnedIds = new Set(badgeData?.earned_badges?.map(b => b.badge_id) || []);

  if (loading && !badgeData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="achievements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Trophy className="text-amber-500" /> My Achievements
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Celebrate your milestones and track your progress
          </p>
        </div>
        <Button onClick={checkAchievements} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full gap-2" disabled={checking}>
          <Sparkles size={16} className={checking ? 'animate-spin' : ''} /> 
          {checking ? 'Checking...' : 'Check for New Badges'}
        </Button>
      </div>

      {/* Progress Hero */}
      {badgeData && (
        <Card className="border-0 shadow-lg rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <p className="text-amber-100 text-sm font-medium mb-2">YOUR PROGRESS</p>
                <p className="text-4xl md:text-5xl font-bold">{badgeData.total_earned} / {allBadges?.badges?.length || 24}</p>
                <p className="text-amber-100 mt-1">Badges Earned</p>
                <div className="mt-4 max-w-md">
                  <Progress value={badgeData.progress} className="h-3 bg-white/30" />
                  <p className="text-sm text-amber-100 mt-1">{badgeData.progress}% Complete</p>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center">
                  <Star className="w-12 h-12 text-amber-200" />
                </div>
                <p className="text-2xl font-bold mt-2">{badgeData.total_points}</p>
                <p className="text-amber-100 text-sm">Total Points</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="earned" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="earned" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Earned ({badgeData?.total_earned || 0})
          </TabsTrigger>
          <TabsTrigger value="all" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            All Badges
          </TabsTrigger>
          <TabsTrigger value="next" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Up Next
          </TabsTrigger>
        </TabsList>

        {/* Earned Badges */}
        <TabsContent value="earned" className="space-y-4">
          {badgeData?.earned_badges?.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Badges Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Complete activities to earn your first badge!
                </p>
                <Button onClick={checkAchievements} variant="outline" className="rounded-full">
                  Check for Badges
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {badgeData?.earned_badges?.map(badge => {
                const details = badge.details || {};
                const IconComponent = getIcon(details.icon);
                return (
                  <Card key={badge.id} className="border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedBadge({ ...details, earned_at: badge.earned_at, earned: true })} data-testid={`achievement-card-${badge.id}`}>
                    <CardContent className="p-4 text-center">
                      <div 
                        className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
                        style={{ backgroundColor: `${details.color}20` }}
                      >
                        <IconComponent 
                          className="w-8 h-8" 
                          style={{ color: details.color }} 
                        />
                      </div>
                      <h3 className="font-semibold text-sm">{details.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{details.description}</p>
                      <Badge className="mt-2" style={{ backgroundColor: `${details.color}20`, color: details.color }}>
                        +{details.points} pts
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-2">
                        Earned {formatDate(badge.earned_at)}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* All Badges by Category */}
        <TabsContent value="all" className="space-y-6">
          {allBadges?.by_category && Object.entries(allBadges.by_category).map(([category, badges]) => (
            <Card key={category} className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold capitalize" style={{ fontFamily: 'Manrope' }}>
                  {category.replace(/_/g, ' ')} Badges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {badges.map(badge => {
                    const isEarned = earnedIds.has(badge.id);
                    const IconComponent = getIcon(badge.icon);
                    return (
                      <button
                        type="button"
                        key={badge.id}
                        className={`p-3 rounded-xl text-center transition-all ${isEarned ? 'bg-amber-50' : 'bg-muted/30 opacity-60'} hover:shadow-sm`}
                        onClick={() => setSelectedBadge({ ...badge, earned: isEarned })}
                        data-testid={`achievement-all-${badge.id}`}
                      >
                        <div 
                          className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center relative ${isEarned ? '' : 'grayscale'}`}
                          style={{ backgroundColor: isEarned ? `${badge.color}20` : '#e5e7eb' }}
                        >
                          <IconComponent 
                            className="w-6 h-6" 
                            style={{ color: isEarned ? badge.color : '#9ca3af' }} 
                          />
                          {!isEarned && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center">
                              <Lock className="w-3 h-3 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs font-medium truncate">{badge.name}</p>
                        <p className="text-xs text-muted-foreground">+{badge.points}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Up Next */}
        <TabsContent value="next" className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                Badges You Can Earn Next
              </CardTitle>
            </CardHeader>
            <CardContent>
              {badgeData?.available_badges?.length === 0 ? (
                <div className="text-center py-8">
                  <Crown className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">You've earned all available badges!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {badgeData?.available_badges?.map(badge => {
                    const IconComponent = getIcon(badge.icon);
                    return (
                      <button key={badge.id} className="flex items-center gap-4 p-3 bg-muted/30 rounded-xl w-full text-left hover:bg-muted/50" onClick={() => setSelectedBadge({ ...badge, earned: false })} data-testid={`achievement-next-${badge.id}`}>
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${badge.color}20` }}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: badge.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{badge.name}</p>
                          <p className="text-sm text-muted-foreground">{badge.description}</p>
                        </div>
                        <Badge variant="outline" className="shrink-0">+{badge.points} pts</Badge>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedBadge} onOpenChange={(open) => !open && setSelectedBadge(null)}>
        <DialogContent className="max-w-2xl" data-testid="achievement-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selectedBadge?.name || selectedBadge?.title}</DialogTitle>
          </DialogHeader>
          {selectedBadge && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">{selectedBadge.description || 'Details coming soon.'}</p>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedBadge.earned ? 'Earned' : 'Not earned yet'}</Badge>
                {selectedBadge.points && <Badge variant="outline">+{selectedBadge.points} pts</Badge>}
              </div>
              {selectedBadge.earned_at && (
                <p className="text-xs text-muted-foreground">Earned {formatDate(selectedBadge.earned_at)}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
